import { useEffect, useMemo, useState, useCallback } from "react"
import {
  Box,
  HStack,
  Separator,
  Stack,
  Table,
  Tooltip,
} from "@chakra-ui/react"
import { FiInfo } from "react-icons/fi"
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ReferenceLine } from "recharts"
import { Button, Text, DollarInput } from "@/components/ui"
import { markOfferViewed } from "@/api"
import { useIsMobile } from "@/hooks"
import { SCHEDULE_CLOSING_MEETING_STEP_INDEX } from "@/constants"
import { DealType, EquityType, Offer } from "@/types"
import { formatDollar } from "@/utils"
import { DecisionOfferModal } from "./"

interface Props {
  dealId: number
  offer: Offer
  currentStepIndex: number
  dealType?: DealType
  isReadOnly?: boolean
  // Another (or this) offer on the deal is already accepted: no Accept/Decline here.
  hasAcceptedOffer?: boolean
}

const OfferBox = ({ label, value, description, badge, borderColor = "brand.25", badgeBg, badgeColor }: {
  label: string
  value: string
  description?: string
  badge?: string
  borderColor?: string
  badgeBg?: string
  badgeColor?: string
}) => (
  <Stack flex={1} p={5} borderRadius={10} bg="white" boxShadow="md" borderLeftWidth="4px" borderLeftColor={borderColor} gap={1}>
    <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wide">{label}</Text>
    <Text fontSize="2xl" fontWeight="bold" color="black">{value}</Text>
    {description && <Text fontSize="xs" color="gray.500">{description}</Text>}
    {badge && (
      <Box mt={1}>
        <Text as="span" fontSize="xs" fontWeight="semibold" px={2} py={0.5} borderRadius="full" bg={badgeBg ?? "teal.50"} color={badgeColor ?? "teal.700"}>
          {badge}
        </Text>
      </Box>
    )}
  </Stack>
)

const InfoTooltip = ({ label }: { label: string }) => (
  <Tooltip.Root openDelay={0} closeDelay={100}>
    <Tooltip.Trigger asChild>
      <Box as="span" cursor="pointer" display="inline-flex" alignItems="center" ml={1}>
        <FiInfo size={14} color="#718096" />
      </Box>
    </Tooltip.Trigger>
    <Tooltip.Positioner>
      <Tooltip.Content bg="gray.700" color="white" px={3} py={2} borderRadius="md" maxW="300px" fontSize="xs">
        <Tooltip.Arrow>
          <Tooltip.ArrowTip />
        </Tooltip.Arrow>
        {label}
      </Tooltip.Content>
    </Tooltip.Positioner>
  </Tooltip.Root>
)

export const OfferComponent = ({ dealId, offer, currentStepIndex, dealType, isReadOnly, hasAcceptedOffer }: Props) => {
  const isMobile = useIsMobile()
  const isRsu = dealType === "RSU"
  const [hypotheticalSharePrice, setHypotheticalSharePrice] = useState<number>(0)
  const [offerState, setOfferState] = useState<"pending" | "accepted" | "declined">("pending")
  const [isAcceptOfferModalOpen, setIsAcceptOfferModalOpen] = useState<boolean>(false)
  const [viewIncentive, setViewIncentive] = useState<boolean>(false)

  useEffect(() => {
    markOfferViewed({ dealId, offerNumber: offer.offerNumber })
  }, [dealId, offer.offerNumber])

  const hasIncentive = offer.isIncentive === 1 && offer.incentivePp != null && offer.incentiveLp != null
  const effectivePp = viewIncentive && hasIncentive ? offer.incentivePp! : offer.pp
  const effectiveLp = viewIncentive && hasIncentive ? offer.incentiveLp! : offer.lp

  const { grossProceeds, netFinalDistribution, netProceeds } = useMemo(() => {
    const { equities, desiredLiquidity } = offer
    const pp = effectivePp
    const lp = effectiveLp
    const totalShares = equities.reduce((partialSum, equity) => partialSum + equity.coveredEquityAmount, 0)
    const grossProceeds = totalShares * hypotheticalSharePrice
    const esoParticipation = grossProceeds * pp
    const distribution = grossProceeds - esoParticipation - lp
    const esoGrossProceeds = grossProceeds < lp ? grossProceeds : grossProceeds * pp + lp
    const netFinalDistribution = distribution > 0 ? distribution : 0
    let nsoTax = 0
    for (let i = 0; i < equities.length; i++) {
      const coveredEquity = equities[i]
      const { type, tax } = coveredEquity.equity
      if (type === EquityType.NSO) {
        nsoTax += tax
      }
    }

    const netProceeds = desiredLiquidity + netFinalDistribution + nsoTax
    return { grossProceeds, netFinalDistribution, netProceeds, esoGrossProceeds }
  }, [hypotheticalSharePrice, offer, effectivePp, effectiveLp])

  const { chartData, breakEvenPrice } = useMemo(() => {
    const { equities, desiredLiquidity, exerciseCost } = offer
    const pp = effectivePp
    const lp = effectiveLp
    const totalShares = equities.reduce((s, eq) => s + eq.coveredEquityAmount, 0)
    const totalPayment = (exerciseCost ?? 0) + desiredLiquidity
    let nsoTax = 0
    for (const eq of equities) {
      if (eq.equity.type === EquityType.NSO) nsoTax += eq.equity.tax
    }

    // Break-even: share price where client distribution begins
    // b/e price = LP / [(1-PP) * totalShares]
    let breakEvenPrice = -1
    if (pp < 1 && totalShares > 0) {
      const candidate = lp / ((1 - pp) * totalShares)
      if (candidate > 0 && isFinite(candidate)) breakEvenPrice = candidate
    }

    // Determine chart range
    const maxAnchor = Math.max(hypotheticalSharePrice, breakEvenPrice > 0 ? breakEvenPrice : 0, 10)
    const maxPrice = maxAnchor * 2
    const step = maxPrice / 20

    const priceSet = new Set<number>()
    for (let i = 0; i <= 20; i++) priceSet.add(Math.round(i * step * 100) / 100)
    if (breakEvenPrice > 0) priceSet.add(Math.round(breakEvenPrice * 100) / 100)
    if (hypotheticalSharePrice > 0) priceSet.add(hypotheticalSharePrice)

    const prices = Array.from(priceSet).sort((a, b) => a - b)

    const chartData = prices.map(price => {
      const gp = totalShares * price
      const esoGross = gp < lp ? gp : gp * pp + lp
      const esoNet = esoGross - totalPayment
      const dist = gp - gp * pp - lp
      const yourTotalPayout = desiredLiquidity + (dist > 0 ? dist : 0) + nsoTax
      return { sharePrice: price, esoNet, yourTotalPayout }
    })

    return { chartData, breakEvenPrice }
  }, [hypotheticalSharePrice, offer, effectivePp, effectiveLp])

  const esoCanMakeMore = useMemo(() => {
    return chartData.some(d => d.esoNet > d.yourTotalPayout)
  }, [chartData])

  useEffect(() => {
    setHypotheticalSharePrice(offer.hypotheticalSharePrice ?? 0)
  }, [offer])

  const handleHypotheticalSharePriceChange = (value: string | number) => {
    const num = Number(value);
    if (!isNaN(num)) {
      setHypotheticalSharePrice(num);
    } else {
      setHypotheticalSharePrice(0);
    }
  }

  const handleAcceptOffer = () => {
    setOfferState("accepted")
    setIsAcceptOfferModalOpen(true)
  }

  const handleDeclineOffer = () => {
    setOfferState("declined")
    setIsAcceptOfferModalOpen(true)
  }

  const isSharesOnly = offer.equities.length > 0 && offer.equities.every(eq => eq.equity.type === EquityType.SHARE || eq.equity.type === EquityType.RSU)

  return (
    <Stack w="100%" gap={5} mb={10} px={{ base: 4, md: 10 }} pb={20}>
      {
        currentStepIndex >= SCHEDULE_CLOSING_MEETING_STEP_INDEX && offer.isAccepted === 1 && (
          <Text fontSize="lg" fontWeight="bold" color="green.600" mt={5}>
            You have accepted this offer.
          </Text>
        )
      }
      {hasIncentive && (
        <Box position="sticky" top={0} zIndex={10} bg="white" py={3} mx={{ base: -4, md: -10 }} px={{ base: 4, md: 10 }} borderBottom="1px solid" borderColor="gray.200" mt={currentStepIndex >= SCHEDULE_CLOSING_MEETING_STEP_INDEX && offer.isAccepted === 1 ? 0 : 10}>
          <HStack justifyContent="center">
            <HStack gap={0} borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="gray.100">
              <Button
                size="sm"
                variant="ghost"
                borderRadius={0}
                bg={!viewIncentive ? "white" : "transparent"}
                color={!viewIncentive ? "black" : "gray.500"}
                fontWeight={!viewIncentive ? "bold" : "normal"}
                boxShadow={!viewIncentive ? "sm" : "none"}
                onClick={() => setViewIncentive(false)}
              >
                Standard
              </Button>
              <Button
                size="sm"
                variant="ghost"
                borderRadius={0}
                bg={viewIncentive ? "white" : "transparent"}
                color={viewIncentive ? "black" : "gray.500"}
                fontWeight={viewIncentive ? "bold" : "normal"}
                boxShadow={viewIncentive ? "sm" : "none"}
                onClick={() => setViewIncentive(true)}
              >
                Incentive
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}
      <Stack w="100%" p={5} borderRadius={10} bg="gray.50" boxShadow="sm" mt={hasIncentive ? 0 : (currentStepIndex >= SCHEDULE_CLOSING_MEETING_STEP_INDEX && offer.isAccepted === 1 ? 0 : 10)}>
        <Text fontSize="md" fontWeight="bold">ESO Offer Details</Text>
        {viewIncentive && hasIncentive && offer.incentiveDeadline && (
          <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" px={4} py={2}>
            <Text fontSize="sm" color="blue.700">
              Incentive terms apply if your deal exits by{" "}
              <Text as="span" fontWeight="bold">
                {new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(new Date(offer.incentiveDeadline))}
              </Text>
              . Otherwise, standard terms apply.
            </Text>
          </Box>
        )}
        {isSharesOnly ? (
          <Stack direction={{ base: "column", md: "row" }} w="100%">
            <OfferBox
              label="Total Up Front Payment"
              value={formatDollar(offer.desiredLiquidity)}
              description="Personal liquidity"
              badge="Sent at closing"
              borderColor="orange.100"
              badgeBg="orange.50"
              badgeColor="orange.700"
            />
          </Stack>
        ) : (
          <Stack direction={{ base: "column", md: "row" }} w="100%">
            <OfferBox
              label="Exercise Payment"
              value={offer.exerciseCost != null ? formatDollar(offer.exerciseCost) : "N/A"}
              description="Covers the cost of exercising your options"
              badge="Sent at closing"
            />
            <OfferBox
              label="Liquidity Payment"
              value={formatDollar(offer.desiredLiquidity)}
              description="Personal liquidity for taxes or other use"
              badge="Sent after exercise confirmed"
            />
            <OfferBox
              label="Total Up Front Payment"
              value={formatDollar((offer.exerciseCost ?? 0) + offer.desiredLiquidity)}
              description="Total ESO Investment"
              badge="Combined total"
              borderColor="orange.100"
              badgeBg="orange.50"
              badgeColor="orange.700"
            />
          </Stack>
        )}
        <Separator my={5} />
        <Stack direction={{ base: "column", md: "row" }} w="100%">
          <Stack flex={1} p={5} borderRadius={10} bg="white" boxShadow="md" gap={1}>
            <HStack>
              <Text fontSize="sm" color="gray.600">Participation Percentage (PP)</Text>
              <InfoTooltip label="ESO's percentage of all gross proceeds at exit, whether received as cash or shares" />
            </HStack>
            <Text fontSize="2xl" fontWeight="bold">{`${(offer.pp * 100).toLocaleString()}%`}</Text>
            {hasIncentive && (
              <Text fontSize="sm" color="blue.600" fontWeight="semibold">{`Incentive: ${(offer.incentivePp! * 100).toLocaleString()}%`}</Text>
            )}
          </Stack>
          <Stack flex={1} p={5} borderRadius={10} bg="white" boxShadow="md" gap={1}>
            <HStack>
              <Text fontSize="sm" color="gray.600">Liquidation Preference (LP)</Text>
              <InfoTooltip label="ESO's right to recover a fixed payment from your remaining share of proceeds at exit — separate from and in addition to the Participation Percentage" />
            </HStack>
            <Text fontSize="2xl" fontWeight="bold">{formatDollar(offer.lp)}</Text>
            {hasIncentive && (
              <Text fontSize="sm" color="blue.600" fontWeight="semibold">{`Incentive: ${formatDollar(offer.incentiveLp!)}`}</Text>
            )}
          </Stack>
        </Stack>
        <Text fontSize="xs" color="gray.500" fontStyle="italic" mt={3} px={2}>
          This transaction is non-recourse, so you are not required to personally guarantee payments to ESO if the underlying stock is unsuccessful. Title to the stock will remain in your name and you will retain voting, information, and other privileges associated with stock ownership.
        </Text>
      </Stack>

      <Stack pt={5} pb={5} gap={8}>
        <Text fontSize="sm" fontWeight="bold">Your Grants</Text>

        {offer.equities.some(eq => eq.equity.type === EquityType.ISO || eq.equity.type === EquityType.NSO) && (
          <Box overflowX="auto">
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader><Text fontWeight="bold" fontSize="sm">Date</Text></Table.ColumnHeader>
                  <Table.ColumnHeader><Text fontWeight="bold" fontSize="sm">Type</Text></Table.ColumnHeader>
                  <Table.ColumnHeader><Text fontWeight="bold" fontSize="sm">Description</Text></Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right"><Text fontWeight="bold" fontSize="sm">Strike Price</Text></Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right"><Text fontWeight="bold" fontSize="sm">{isMobile ? "Shares" : "Number of Shares"}</Text></Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {offer.equities.filter(eq => eq.equity.type === EquityType.ISO || eq.equity.type === EquityType.NSO).map((eq, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>
                      <Text fontSize="sm">{eq.equity.issueDate && !eq.equity.issueDate.startsWith("1899") ? new Intl.DateTimeFormat("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }).format(new Date(eq.equity.issueDate)) : "—"}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{eq.equity.type?.toUpperCase()}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{eq.equity.name}</Text>
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <Text fontSize="sm">{formatDollar(eq.equity.strikePrice)}</Text>
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <Text fontSize="sm">{eq.coveredEquityAmount.toLocaleString()}</Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}

        {offer.equities.some(eq => eq.equity.type === EquityType.RSU || eq.equity.type === EquityType.SHARE) && (
          <Box overflowX="auto">
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader><Text fontWeight="bold" fontSize="sm">Date</Text></Table.ColumnHeader>
                  <Table.ColumnHeader><Text fontWeight="bold" fontSize="sm">Type</Text></Table.ColumnHeader>
                  <Table.ColumnHeader><Text fontWeight="bold" fontSize="sm">Description</Text></Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right"><Text fontWeight="bold" fontSize="sm">{isMobile ? "Shares" : "Number of Shares"}</Text></Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {offer.equities.filter(eq => eq.equity.type === EquityType.RSU || eq.equity.type === EquityType.SHARE).map((eq, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>
                      <Text fontSize="sm">{eq.equity.issueDate && !eq.equity.issueDate.startsWith("1899") ? new Intl.DateTimeFormat("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }).format(new Date(eq.equity.issueDate)) : "—"}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{isRsu && eq.equity.type === EquityType.SHARE ? "RSU" : eq.equity.type?.toUpperCase()}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{eq.equity.name}</Text>
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <Text fontSize="sm">{eq.coveredEquityAmount.toLocaleString()}</Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}

        <Text fontSize="sm" fontWeight="bold" pt={3}>
          Total Shares: {offer.equities.reduce((sum, eq) => sum + eq.coveredEquityAmount, 0).toLocaleString()}
        </Text>
      </Stack>
      <Separator />
      <Text fontSize="xl" fontWeight="bold" mt={5}>Offer Distribution Details</Text>
      <Stack direction={{ base: "column", md: "row" }} w="100%" mb={5}>
        <Stack w={{ base: "100%", md: "25%" }} h="fit-content" p={5} borderRadius={10} bg="gray.50" boxShadow="sm">
          <Text fontSize="sm" pb={5}>Enter a share price to see how much you would make</Text>
          <Text fontSize="sm" fontWeight="bold">Hypothetical Exit Share Price</Text>
          <DollarInput value={hypotheticalSharePrice} setValue={handleHypotheticalSharePriceChange} />
        </Stack>

        <Stack w={{ base: "100%", md: "75%" }} pl={{ base: 0, md: 5 }}>
          <Stack direction={{ base: "column", md: "row" }} w="100%">
            <OfferBox
              label="Gross Proceeds"
              description={"(Total Shares x Hypothetical Exit Share Price)"}
              value={formatDollar(grossProceeds)}
              borderColor="#FFC043"
            />
            <OfferBox
              label="ESO Proceeds"
              description={grossProceeds < effectiveLp
                ? `(Capped at Gross Proceeds)`
                : `(${(effectivePp * 100).toLocaleString()}% PP: ${formatDollar(grossProceeds * effectivePp)} + LP: ${formatDollar(effectiveLp)})`}
              value={formatDollar(grossProceeds < effectiveLp ? grossProceeds : grossProceeds * effectivePp + effectiveLp)}
            />
          </Stack>
          <Stack direction={{ base: "column", md: "row" }} w="100%">
            <OfferBox
              label="Your Net Final Distribution"
              description={"(Gross Proceeds - ESO Proceeds)"}
              value={formatDollar(netFinalDistribution)}
              borderColor="#008081"
            />
            <OfferBox
              label="Your Total Payout"
              description={isSharesOnly
                ? "(Total Up Front Payment + Your Net Final Distribution)"
                : "(Liquidity Payment + Your Net Final Distribution)"}
              value={formatDollar(netProceeds)}
              borderColor="#3CC68A"
            />
          </Stack>
        </Stack>
      </Stack>

      <Stack w="100%" p={5} borderRadius={10} bg="gray.50" boxShadow="sm">
        <Text fontSize="sm" fontWeight="bold" pb={3}>Distribution Comparison</Text>
        <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
          <LineChart data={chartData} margin={isMobile ? { top: 5, right: 10, left: -10, bottom: 15 } : { top: 10, right: 30, left: 20, bottom: 20 }}>
            <XAxis
              dataKey="sharePrice"
              tickFormatter={(v: number) => `$${v.toLocaleString()}`}
              label={{ value: "Share Price", position: "insideBottom", offset: -10, style: { fontSize: isMobile ? 10 : 12 } }}
              interval={Math.max(Math.ceil(chartData.length / (isMobile ? 4 : 8)) - 1, 0)}
              tick={{ fontSize: isMobile ? 9 : 12 }}
            />
            <YAxis
              tickFormatter={(v: number) => {
                const abs = Math.abs(v)
                if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`
                return `$${(v / 1000).toFixed(0)}k`
              }}
              tick={{ fontSize: isMobile ? 9 : 12 }}
              width={isMobile ? 40 : 60}
            />
            <RechartsTooltip
              formatter={(value) => formatDollar(value as number)}
              labelFormatter={(label) => `Share Price: ${formatDollar(label as number)}`}
            />
            <Legend verticalAlign="top" wrapperStyle={isMobile ? { fontSize: 10 } : undefined} />
            <Line type="monotone" dataKey="esoNet" name={isMobile ? "ESO" : "ESO Net Proceeds"} stroke="#3F5EA1" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="yourTotalPayout" name={isMobile ? "You" : "Your Total Payout"} stroke="#3CC68A" strokeWidth={2} dot={false} />
            {breakEvenPrice > 0 && (
              <ReferenceLine
                x={Math.round(breakEvenPrice * 100) / 100}
                stroke="#38A169"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={isMobile ? undefined : { value: `Break-even: ${formatDollar(breakEvenPrice)}`, position: "top", style: { fontSize: 11, fontWeight: "bold", fill: "#38A169" } }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Stack>
      <Stack gap={1} px={2}>
        <Text fontSize="xs" color="gray.500">
          The graph above illustrates the returns this offer would generate over a range of exit share prices.
        </Text>
        {esoCanMakeMore && (
          <Text fontSize="xs" color="gray.500">
            Note that ESO can make more than you, depending on the final exit price.
          </Text>
        )}
      </Stack>

      <DecisionOfferModal
        isAccept={offerState === "accepted"}
        dealId={dealId}
        offerNumber={offer.offerNumber}
        isOpen={isAcceptOfferModalOpen}
        onClose={() => setIsAcceptOfferModalOpen(false)}
      />

      {!isReadOnly && hasAcceptedOffer && offer.isAccepted !== 1 && currentStepIndex < SCHEDULE_CLOSING_MEETING_STEP_INDEX && (
        <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" px={4} py={3} mt={4}>
          <Text fontSize="sm" color="gray.600">
            You have already accepted another offer on this deal. If you would like to switch to this one, please contact your deal manager.
          </Text>
        </Box>
      )}

      {!isReadOnly && !hasAcceptedOffer && currentStepIndex < SCHEDULE_CLOSING_MEETING_STEP_INDEX && offer.isAccepted !== 1 && offerState === "pending" && (
        <Box position="sticky" bottom={0} bg="white" borderTop="1px solid" borderColor="gray.200" py={4} mx={{ base: -4, md: -10 }} px={{ base: 4, md: 10 }} zIndex={10}>
          <Stack align="center" gap={2}>
            <Text fontSize="sm" color="gray.500">Ready to move forward with your offer?</Text>
            <HStack>
              <Button
                variant="solid"
                disabled={offer.isAccepted === 1}
                onClick={handleAcceptOffer}
              >
                {offer.isAccepted ? "Accepted" : "Accept Offer"}
              </Button>
              <Button variant="outline" onClick={handleDeclineOffer}>
                Decline Offer
              </Button>
            </HStack>
          </Stack>
        </Box>
      )}
    </Stack>
  )
}
